/**
 * 錯誤映射（zh）。
 *
 * 後端（api/app/api/deps.py、api/app/api/v1/auth.py）遇到的認證/授權錯誤已回傳
 * 繁中 `{"detail": "..."}`（如「帳號或密碼不正確」「token 無效或已過期」），這裡直接
 * 沿用該訊息；沒有 detail 或非 JSON（網路中斷、CORS 被擋、502 等）時才落回下面的
 * HTTP 狀態碼通用 zh 訊息表。
 */

const STATUS_MESSAGES: Record<number, string> = {
  400: "請求格式錯誤，請確認輸入內容",
  401: "登入已失效，請重新登入",
  403: "權限不足，無法執行此操作",
  404: "找不到資源",
  409: "資料已被異動，請重新整理後再試",
  422: "資料格式不正確，請檢查欄位內容",
  429: "操作過於頻繁，請稍後再試",
  500: "伺服器發生錯誤，請稍後再試",
  502: "服務暫時無法連線，請稍後再試",
  503: "服務暫時無法使用，請稍後再試",
  504: "伺服器回應逾時，請稍後再試",
};

export const NETWORK_ERROR_MESSAGE = "無法連線到伺服器，請確認網路連線後再試";

/** 統一的 API 錯誤型別：status=0 代表 fetch 本身失敗（網路/CORS），非 HTTP 狀態碼。 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * pydantic v2 `ValidationError` 原生序列化成 422 body 的那種陣列形狀
 * （每筆 `{loc: (string|number)[], msg: string, type: string}`）。
 *
 * 正規修法是後端把 body 宣告成 `dict[str, Any]`＋`app.core.validation_messages
 * .humanize()` 手動組單一字串 `detail`（`finance_write.py` / `attendance.py` /
 * `member_auth.py` 已是這套慣例）；FastAPI 對「直接把 pydantic model 當 body
 * 型別」的端點仍會在 handler 之前自己跑驗證、422 detail 回這種陣列——這裡是
 * **後盾**：萬一哪個端點忘了套用上面那套慣例，至少別讓使用者看到通用的
 * 「資料格式不正確」，儘量把陣列第一筆的訊息＋欄位名拼成一句話。
 */
interface PydanticErrorItem {
  loc?: unknown;
  msg?: unknown;
  type?: unknown;
}

function isPydanticErrorArray(detail: unknown): detail is PydanticErrorItem[] {
  return (
    Array.isArray(detail) &&
    detail.length > 0 &&
    detail.every((item) => item !== null && typeof item === "object" && "msg" in item)
  );
}

/** pydantic v2 對 `model_validator` 拋出的例外固定加的英文前綴，剝掉留下我們自己寫的中文。 */
function stripPydanticWrapperPrefix(msg: string): string {
  return msg.replace(/^Value error,\s*/, "").replace(/^Assertion failed,\s*/, "");
}

function formatPydanticErrorItem(item: PydanticErrorItem): string {
  const msg = stripPydanticWrapperPrefix(typeof item.msg === "string" ? item.msg : "").trim();
  const loc = Array.isArray(item.loc) ? item.loc : [];
  // loc 常見形狀是 ["body", "fieldName", ...]；取第一個非 "body" 的字串當欄位名。
  const field = loc.find((seg): seg is string => typeof seg === "string" && seg !== "body");
  if (field && msg) {
    return `${field}：${msg}`;
  }
  return msg;
}

/** 由 HTTP 狀態碼 + 後端回傳的 body 組出使用者看得懂的繁中訊息。 */
export function mapErrorMessage(status: number, body: unknown): string {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (isPydanticErrorArray(detail)) {
      const message = formatPydanticErrorItem(detail[0]);
      if (message) {
        return message;
      }
    }
  }
  return STATUS_MESSAGES[status] ?? `發生未預期的錯誤（狀態碼 ${status}）`;
}
